import React, { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PropertyCard } from "@/components/PropertyCard";
import { searchProperties } from "@/components/PropertySearch";
import { useApp } from "@/context/AppContext";
import { MICRO_MARKETS, PROPERTY_TYPES, STAGES } from "@/data/seed";
import { VERTICALS } from "@/lib/perspective";

const BUDGETS = [
  { label: "Any Budget", min: 0, max: Infinity },
  { label: "Under ₹1 Cr", min: 0, max: 10000000 },
  { label: "₹1 – 2 Cr", min: 10000000, max: 20000000 },
  { label: "₹2 – 4 Cr", min: 20000000, max: 40000000 },
  { label: "₹4 Cr +", min: 40000000, max: Infinity },
];

const SORTS = ["Recommended", "Price Low–High", "Price High–Low", "Newest", "Possession"];

export default function Properties() {
  const { data } = useApp();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  useEffect(() => { setQ(params.get("q") || ""); }, [params]);
  const [vertical, setVertical] = useState("all");
  const [type, setType] = useState("all");
  const [budget, setBudget] = useState(0);
  const [location, setLocation] = useState("all");
  const [stage, setStage] = useState("all");
  const [sort, setSort] = useState("Recommended");
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    let list = data.properties.filter((p) => p.published);
    if (q.trim()) list = searchProperties(list, q);
    if (vertical !== "all") list = list.filter((p) => p.category.includes(vertical));
    if (type !== "all") list = list.filter((p) => p.propertyType === type);
    if (location !== "all") list = list.filter((p) => p.microMarket === location);
    if (stage !== "all") list = list.filter((p) => p.constructionStage === stage);
    const b = BUDGETS[budget];
    list = list.filter((p) => p.priceMin >= b.min && p.priceMin <= b.max);

    const sorted = [...list];
    if (sort === "Price Low–High") sorted.sort((a, b2) => a.priceMin - b2.priceMin);
    else if (sort === "Price High–Low") sorted.sort((a, b2) => b2.priceMin - a.priceMin);
    else if (sort === "Newest") sorted.sort((a, b2) => new Date(b2.createdAt) - new Date(a.createdAt));
    else if (sort === "Possession") sorted.sort((a, b2) => (a.possession > b2.possession ? 1 : -1));
    else sorted.sort((a, b2) => (b2.featured === true) - (a.featured === true));
    return sorted;
  }, [data.properties, q, vertical, type, budget, location, stage, sort]);

  const clearAll = () => { setQ(""); setVertical("all"); setType("all"); setBudget(0); setLocation("all"); setStage("all"); setSort("Recommended"); };

  const selectCls = "rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-gold)]";

  return (
    <div className="container-arch pt-28 pb-24">
      <Reveal>
        <div className="overline">Properties</div>
        <h1 className="mt-3 font-serif text-4xl font-light text-[var(--text-primary)] sm:text-5xl">Explore with perspective</h1>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <p className="max-w-xl text-[var(--text-muted)]">Search, filter and sort — every control works. Each property opens as an Archedia Property Brief.</p>
          <Link to="/request" data-testid="properties-find-my-property" className="btn-gold !py-2.5 text-xs">Find My Property</Link>
        </div>
      </Reveal>

      <div className="sticky top-[72px] z-30 mt-8 rounded-2xl border border-[var(--border-subtle)] bg-[var(--glass-bg)] p-4 backdrop-blur-xl">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="property-search" placeholder="Search project, developer, location, micro-market" className="w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] py-2.5 pl-9 pr-3 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-gold)]" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters" className="btn-outline-gold !px-4 !py-2.5 text-xs"><SlidersHorizontal className="h-4 w-4" /> Filters</button>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="property-sort" className={selectCls}>
              {SORTS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 grid gap-3 border-t border-[var(--border-subtle)] pt-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="filter-panel">
            <select value={vertical} onChange={(e) => setVertical(e.target.value)} data-testid="filter-vertical" className={selectCls}>
              <option value="all">All Verticals</option>
              {Object.values(VERTICALS).map((v) => <option key={v.key} value={v.key}>{v.name}</option>)}
            </select>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="filter-type" className={selectCls}>
              <option value="all">All Types</option>
              {PROPERTY_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <select value={budget} onChange={(e) => setBudget(Number(e.target.value))} data-testid="filter-budget" className={selectCls}>
              {BUDGETS.map((b, i) => <option key={b.label} value={i}>{b.label}</option>)}
            </select>
            <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="filter-location" className={selectCls}>
              <option value="all">All Locations</option>
              {MICRO_MARKETS.map((m) => <option key={m}>{m}</option>)}
            </select>
            <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="filter-stage" className={selectCls}>
              <option value="all">All Stages</option>
              {STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-[var(--text-muted)]" data-testid="results-count">{filtered.length} propert{filtered.length === 1 ? "y" : "ies"}</p>
        <button onClick={clearAll} data-testid="clear-filters" className="inline-flex items-center gap-1 text-sm text-[var(--accent-gold)]"><X className="h-3.5 w-3.5" /> Clear</button>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-16 rounded-2xl border border-dashed border-[var(--border-subtle)] py-20 text-center" data-testid="empty-state">
          <p className="font-serif text-2xl text-[var(--text-primary)]">{q.trim() ? `No properties match “${q.trim()}”.` : "No properties match your perspective yet."}</p>
          <p className="mt-2 text-sm text-[var(--text-muted)]">Adjust your search or filters — or tell us what you need and we'll curate it.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3"><button onClick={clearAll} className="btn-outline-gold">Clear filters</button><Link to="/request" className="btn-gold">Find My Property</Link></div>
        </div>
      ) : (
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
